import React, { useState, useEffect, useRef } from 'react';
import { generateTexts } from '../utils/ai';
import { createTextsImportString } from '../utils/importFormat';

export default function Generator({ t, lang }) {
  const [prompt, setPrompt] = useState('');
  const [textType, setTextType] = useState('say');
  const [count, setCount] = useState(10);
  const [textLang, setTextLang] = useState(lang);

  useEffect(() => { setTextLang(lang); }, [lang]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [editIndex, setEditIndex] = useState(-1);
  const [editValue, setEditValue] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const cooldownRef = useRef(null);

  const handleGenerate = async () => {
    if (!prompt.trim()) return;
    setLoading(true);
    setError('');
    setCopied(false);
    try {
      const langLabel = t.languages[textLang] || textLang;
      const result = await generateTexts(prompt, count, langLabel, textType);
      setMessages(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setCooldown(10);
      cooldownRef.current = setInterval(() => {
        setCooldown(prev => {
          if (prev <= 1) {
            clearInterval(cooldownRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
  };

  const handleCopy = () => {
    if (messages.length === 0) return;
    const importStr = createTextsImportString(messages);
    navigator.clipboard.writeText(importStr);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDelete = (index) => {
    setMessages(prev => prev.filter((_, i) => i !== index));
  };

  const handleEditStart = (index) => {
    setEditIndex(index);
    setEditValue(messages[index]);
  };

  const handleEditSave = () => {
    if (editIndex >= 0 && editValue.trim()) {
      setMessages(prev => prev.map((msg, i) => i === editIndex ? editValue.trim() : msg));
    }
    setEditIndex(-1);
    setEditValue('');
  };

  return (
    <div>
      <div style={{ marginBottom: '16px' }}>
        <label className="wow-label">{t.textTypeLabel}</label>
        <div style={{ display: 'flex', gap: '20px', marginBottom: '16px' }}>
          {[
            { key: 'say', label: t.textTypeSay },
            { key: 'emote', label: t.textTypeEmote },
          ].map(({ key, label }) => (
            <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: textType === key ? 'var(--wow-gold)' : 'var(--wow-text-sec)' }}>
              <input
                type="radio"
                name="textType"
                value={key}
                checked={textType === key}
                onChange={() => setTextType(key)}
                style={{ accentColor: 'var(--wow-gold)' }}
              />
              {label}
            </label>
          ))}
        </div>
      </div>

      <div style={{ marginBottom: '16px' }}>
        <label className="wow-label">{t.promptLabel}</label>
        <textarea
          className="wow-textarea"
          rows={3}
          placeholder={t.promptPlaceholder}
          value={prompt}
          onChange={e => setPrompt(e.target.value)}
        />
      </div>

      <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end', marginBottom: '24px', flexWrap: 'wrap' }}>
        <div>
          <label className="wow-label">{t.countLabel}</label>
          <select
            className="wow-input"
            style={{ width: '80px' }}
            value={count}
            onChange={e => setCount(parseInt(e.target.value))}
          >
            {[1,2,3,4,5,6,7,8,9,10].map(n => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="wow-label">{t.languageLabel}</label>
          <select
            className="wow-input"
            style={{ width: '140px' }}
            value={textLang}
            onChange={e => setTextLang(e.target.value)}
          >
            {Object.entries(t.languages).map(([code, label]) => (
              <option key={code} value={code}>{label}</option>
            ))}
          </select>
        </div>
        <button
          className="wow-btn wow-btn-primary"
          onClick={handleGenerate}
          disabled={loading || !prompt.trim() || cooldown > 0}
        >
          {loading ? t.generating : cooldown > 0 ? `${t.generateBtn} (${cooldown}s)` : t.generateBtn}
        </button>
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: '24px 0' }}>
          <div className="wow-loading" style={{ fontSize: '18px', marginBottom: '8px' }}>&#9881; {t.generating}</div>
          <div style={{ color: 'var(--wow-text-muted)', fontSize: '13px' }}>This may take a few seconds...</div>
        </div>
      )}

      {error && <div className="wow-error">{error}</div>}

      <div className="wow-separator" />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <label className="wow-label" style={{ marginBottom: 0 }}>{t.resultLabel} ({messages.length})</label>
        {messages.length > 0 && (
          <button className="wow-btn" onClick={handleCopy}>
            {copied ? `✓ ${t.copied}` : t.copyImportBtn}
          </button>
        )}
      </div>

      {messages.length === 0 ? (
        <p style={{ color: 'var(--wow-text-muted)' }}>{t.noTexts}</p>
      ) : (
        <div>
          {messages.map((msg, index) => (
            <div key={index} className="wow-text-item">
              <span style={{ color: 'var(--wow-text-muted)', minWidth: '28px', fontSize: '12px' }}>
                #{index + 1}
              </span>
              {editIndex === index ? (
                <input
                  className="wow-input"
                  style={{ flex: 1 }}
                  value={editValue}
                  maxLength={240}
                  onChange={e => setEditValue(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleEditSave(); if (e.key === 'Escape') setEditIndex(-1); }}
                  autoFocus
                />
              ) : (
                <span style={{ flex: 1, fontSize: '14px', color: 'var(--wow-text)' }}>{msg}</span>
              )}
              <div style={{ display: 'flex', gap: '4px' }}>
                {editIndex === index ? (
                  <button className="wow-btn" style={{ padding: '2px 8px', fontSize: '12px' }} onClick={handleEditSave}>✓</button>
                ) : (
                  <button className="wow-btn" style={{ padding: '2px 8px', fontSize: '12px' }} onClick={() => handleEditStart(index)} title={t.editTooltip}>✎</button>
                )}
                <button className="wow-btn wow-btn-danger" style={{ padding: '2px 8px', fontSize: '12px' }} onClick={() => handleDelete(index)} title={t.deleteTooltip}>✕</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: '24px', textAlign: 'center' }}>
          <div className="wow-separator" />
          <form action="https://www.paypal.com/donate" method="post" target="_blank" style={{ display: 'inline-block' }}>
            <input type="hidden" name="business" value="info@schoko-keks.de" />
            <input type="hidden" name="currency_code" value="EUR" />
            <input type="hidden" name="amount" value="1" />
            <input type="hidden" name="item_name" value="RoleplayMessages - AI Text Generator Donation" />
            <input type="image"
              src="https://www.paypalobjects.com/en_US/i/btn/btn_donateCC_LG.gif"
              border="0"
              name="submit"
              title="PayPal - The safer, easier way to pay online!"
              alt="Donate with PayPal button"
              style={{ height: '40px' }}
            />
          </form>
          <p style={{ color: 'var(--wow-text-sec)', fontSize: '14px', marginTop: '12px', lineHeight: '1.6' }}>
            {t.donateText || 'Each text generation costs real money. This is a hobby project — any donation helps keep it running!'}
          </p>
      </div>
    </div>
  );
}
